import { useEffect } from 'react';
import { AlertTriangle, Check, Pencil, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useGetPostByIdQuery } from '@/redux/post/post.api.slice';
import { IJobType, IPostDetailResponse, IUnits } from '@/redux/post/post.d';
import { V2Basics, V2JobDetails } from '../v2Upload.d';
import {
  restoreBasics,
  restoreFiles,
  restoreJobDetails,
  restoreOperations,
} from '../utils/restorePost';
import { missingForSignoff } from '../utils/validation';
import { CARD_SHELL, FIELD_LABEL } from './fileCardStyles';

export type WorkpieceStatus = 'loading' | 'ready' | 'incomplete';

const BASICS_DEFAULTS: V2Basics = {
  title: '',
  description: '',
  category: '',
  material: '',
  difficulty: '',
  license: '',
  tags: [],
};

const JOB_DEFAULTS: V2JobDetails = {
  seeded: false,
  width: '',
  height: '',
  thickness: '',
  length: '',
  diameter: '',
  flipDirection: '',
  units: '',
  jobType: '',
  zZeroPosition: '',
  xyDatumPosition: '',
  softwareUsed: [],
  toolSetter: false,
  autoToolChanger: false,
  isDimensionalMaterial: false,
  dimensionalMaterialLinks: [],
};

// restore* returns Partial; the defaults keep .trim() from throwing
function gapsFor(post: IPostDetailResponse): string[] {
  const files = restoreFiles(post.attachments ?? []);
  return missingForSignoff(
    { ...BASICS_DEFAULTS, ...restoreBasics(post) },
    files,
    restoreOperations(post.modelGroups, files, undefined),
    { ...JOB_DEFAULTS, ...restoreJobDetails(post) }
  ).map((m) => m.label);
}

const dash = (value: string | undefined): string => value || '—';

// fallow-ignore-next-line complexity
function sizeText(post: IPostDetailResponse): string {
  const size = post.jobSize;
  const rotary = post.jobType === IJobType.ROTARY;
  const dims = rotary
    ? [size?.length, size?.diameter]
    : [size?.width, size?.height, size?.thickness];
  if (dims.some((d) => !d)) return '—';
  const unit = post.units === IUnits.INCHES ? 'in' : 'mm';
  return rotary
    ? `${size.length} long × ⌀${size.diameter} ${unit}`
    : `${dims.join(' × ')} ${unit}`;
}

// fallow-ignore-next-line complexity
function StatusBadge({ status }: { status: WorkpieceStatus }) {
  if (status === 'loading') {
    return (
      <span className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-muted-foreground" />
    );
  }
  const ready = status === 'ready';
  const Icon = ready ? Check : AlertTriangle;
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.03em]',
        ready
          ? 'bg-success/15 text-success'
          : 'bg-destructive/15 text-destructive'
      )}
    >
      <Icon className="h-3 w-3" />
      {ready ? 'Ready' : 'Needs work'}
    </span>
  );
}

function MetaCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className={FIELD_LABEL}>{label}</span>
      <span className="truncate text-[13px] font-medium">{value}</span>
    </div>
  );
}

// fallow-ignore-next-line complexity
export default function GroupReviewWorkpieceCard({
  postId,
  index,
  onStatus,
  onEdit,
  onRemove,
  busy,
}: {
  postId: string;
  index: number;
  onStatus: (postId: string, status: WorkpieceStatus) => void;
  onEdit: () => void;
  onRemove: (postId: string) => void;
  busy: boolean;
}) {
  const { data, isError } = useGetPostByIdQuery(postId);

  const gaps = data ? gapsFor(data) : [];
  const loadFailed = isError && !data;
  const status: WorkpieceStatus = (() => {
    if (loadFailed) return 'incomplete';
    if (!data) return 'loading';
    return gaps.length > 0 ? 'incomplete' : 'ready';
  })();

  useEffect(() => {
    onStatus(postId, status);
  }, [postId, status, onStatus]);

  const problems = loadFailed ? ["couldn't be loaded — try reloading"] : gaps;

  return (
    <div
      data-invalid={status === 'incomplete' || undefined}
      className={cn(
        CARD_SHELL,
        'p-4',
        status === 'incomplete' && 'border-destructive/60 bg-destructive/[0.04]'
      )}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <span className={FIELD_LABEL}>Workpiece {index + 1}</span>
          <p className="truncate text-sm font-semibold text-heading">
            {dash(data?.title)}
          </p>
        </div>
        <StatusBadge status={status} />
      </div>

      <div className="mt-3 grid grid-cols-3 gap-3">
        <MetaCell label="Material" value={dash(data?.materialUsed)} />
        <MetaCell
          label="Thickness"
          value={
            data?.jobSize?.thickness ? String(data.jobSize.thickness) : '—'
          }
        />
        <MetaCell label="Size" value={data ? sizeText(data) : '—'} />
      </div>

      {problems.length > 0 && (
        <ul className="mt-3 space-y-1 rounded-lg border border-destructive/40 bg-card p-2.5">
          {problems.map((problem) => (
            <li key={problem} className="text-xs font-medium text-destructive">
              Still needs {problem}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3 flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5"
          onClick={onEdit}
        >
          <Pencil className="h-3.5 w-3.5" />
          Edit
        </Button>
        <span className="grow" />
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 text-muted-foreground hover:text-destructive"
          disabled={busy}
          onClick={() => onRemove(postId)}
        >
          <Trash2 className="h-3.5 w-3.5" />
          Remove
        </Button>
      </div>
    </div>
  );
}
